import { locale } from '../common/localization/localizationService';
import { IRoute } from '../models';
import {
  GenericMapSearchResult,
  GenericMapAgreement,
  GenericMapPlace,
  GenericMapAgreementLine,
  GenericMapRouteLine,
  GenericMapRoute,
  SmartMapGridTypes,
  DisplayedGenericMapElement,
  SmartMapElementType,
} from '../models/GenericMapModels.model';
import { Drawing } from '../services/genericMapService';

export interface ISmartMapGridColumn {
  field: string;
  title: string;
  filter: 'boolean' | 'text' | 'numeric' | 'date';
  width?: string;
  groupable: boolean;
}

export function groupDisplayedElements(
  elements: (
    | GenericMapAgreement
    | GenericMapAgreementLine
    | GenericMapPlace
    | GenericMapRouteLine
  )[]
): DisplayedGenericMapElement[] {
  const groupedElements = elements.reduce((acc, curr) => {
    let lat: number | null = null;
    let lng: number | null = null;
    let routeLineId: string | null = null;

    if ('agreementLatitude' in curr && 'agreementLongitude' in curr) {
      lat = curr.agreementLatitude;
      lng = curr.agreementLongitude;
      routeLineId = curr.routeIds ?? null;
    } else if ('placeLatitude' in curr && 'placeLongitude' in curr) {
      lat = curr.placeLatitude;
      lng = curr.placeLongitude;
      routeLineId = curr.routeIds ?? null;
    } else if (
      'agreementLineLatitude' in curr &&
      'agreementLineLongitude' in curr
    ) {
      lat = curr.agreementLineLatitude;
      lng = curr.agreementLineLongitude;
      routeLineId = curr.routeIds ?? null;
    } else if ('routeLineLatitude' in curr && 'routeLineLongitude' in curr) {
      lat = curr.routeLineLatitude;
      lng = curr.routeLineLongitude;
      routeLineId = curr.routeIds;
    }

    if (lat === null || lng === null) return acc;

    const key = `${lat} - ${lng}`;

    if (!acc[key]) {
      acc[key] = { lat, lng, elements: [], routeIds: [] };
    }

    acc[key].elements.push(curr);

    if (routeLineId !== null && !acc[key].routeIds.includes(routeLineId)) {
      acc[key].routeIds.push(routeLineId);
    }

    return acc;
  }, {} as { [key: string]: DisplayedGenericMapElement });

  return Object.values(groupedElements);
}

export function groupDataByAgreements(
  GenericMapSearchResults: GenericMapSearchResult[],
  routes: IRoute[],
  byRoute = false
): GenericMapAgreement[] | DisplayedGenericMapElement[] {
  if (byRoute) {
    const grouped = GenericMapSearchResults.reduce(
      (acc: { [key: string]: DisplayedGenericMapElement }, curr) => {
        const lat = curr.agreementLatitude;
        const lng = curr.agreementLongitude;
        const routeLineId = curr.route ?? null;
        curr.routeName = getRouteNameFromId(curr, routes);

        if (lat === null || lng === null) return acc;

        const key = `${lat} - ${lng}`;

        if (!acc[key]) {
          acc[key] = {
            lat,
            lng,
            elements: [],
            routeIds: [],
          };
        }

        acc[key].elements.push(curr);

        if (routeLineId !== null && !acc[key].routeIds.includes(routeLineId)) {
          acc[key].routeIds.push(routeLineId);
        }

        return acc;
      },
      {}
    );

    return Object.values(grouped);
  } else {
    const groupedAgreements = GenericMapSearchResults.reduce(
      (acc: { [key: string]: GenericMapAgreement }, curr) => {
        const key = `${curr.paSystem}_${curr.agreementId}`;

        if (!acc[key]) {
          acc[key] = {
            paSystem: curr.paSystem,
            agreementId: curr.agreementId,
            agreementType: curr.agreementType,
            agreementAddress: curr.agreementAddress,
            agreementEstate: curr.agreementEstate,
            agreementLatitude: curr.agreementLatitude,
            agreementLongitude: curr.agreementLongitude,
            routeIds: [curr.route],
            routeNames: [getRouteNameFromId(curr, routes)],
          };
        }

        if (acc[key] && !acc[key].routeIds.includes(curr.route)) {
          acc[key].routeIds.push(curr.route);
          acc[key].routeNames.push(getRouteNameFromId(curr, routes));
        }

        return acc;
      },
      {}
    );

    return Object.values(groupedAgreements);
  }
}

export function groupDataByPlaces(
  GenericMapSearchResults: GenericMapSearchResult[],
  byRoute = false
): GenericMapPlace[] | DisplayedGenericMapElement[] {
  if (byRoute) {
    const grouped = GenericMapSearchResults.reduce(
      (acc: { [key: string]: DisplayedGenericMapElement }, curr) => {
        const lat = curr.placeLatitude;
        const lng = curr.placeLongitude;
        const routeLineId = curr.route ?? null;

        if (lat === null || lng === null) return acc;

        const key = `${lat} - ${lng}`;

        if (!acc[key]) {
          acc[key] = {
            lat,
            lng,
            elements: [],
            routeIds: [],
          };
        }

        acc[key].elements.push(curr);

        if (routeLineId !== null && !acc[key].routeIds.includes(routeLineId)) {
          acc[key].routeIds.push(routeLineId);
        }

        return acc;
      },
      {}
    );

    return Object.values(grouped);
  } else {
    const groupedAgreements = GenericMapSearchResults.reduce(
      (acc: { [key: string]: GenericMapPlace }, curr) => {
        const key = `${curr.paSystem}_${curr.placeNumber}`;

        if (!acc[key]) {
          acc[key] = {
            paSystem: curr.paSystem,
            placeNumber: curr.placeNumber,
            placeLatitude: curr.placeLatitude,
            placeLongitude: curr.placeLongitude,
            placeName: curr.placeName,
            placeType: curr.placeType,
            agreementId: curr.agreementId,
            routeIds: [curr.route],
          };
        }
        if (acc[key] && !acc[key].routeIds.includes(curr.route)) {
          acc[key].routeIds.push(curr.route);
        }
        return acc;
      },
      {}
    );

    const agreementsArray = Object.values(groupedAgreements);
    return agreementsArray;
  }
}

export function groupDataByRoutes(
  GenericMapSearchResults: GenericMapSearchResult[]
): GenericMapRoute[] {
  const groupedAgreements = GenericMapSearchResults.reduce(
    (acc: { [key: string]: GenericMapRoute }, curr) => {
      const key = `${curr.paSystem}_${curr.route}`;

      if (!acc[key]) {
        acc[key] = {
          paSystem: curr.paSystem,
          route: curr.route,
        };
      }
      return acc;
    },
    {}
  );

  const routesArray = Object.values(groupedAgreements);
  return routesArray;
}

export function selectAgreementLines(
  GenericMapSearchResults: GenericMapSearchResult[],
  routes: IRoute[],
  byRoute = false
): GenericMapAgreementLine[] | DisplayedGenericMapElement[] {
  if (byRoute) {
    const grouped = GenericMapSearchResults.reduce(
      (acc: { [key: string]: DisplayedGenericMapElement }, curr) => {
        const lat = curr.agreementLineLatitude || curr.agreementLatitude;
        const lng = curr.agreementLineLongitude || curr.agreementLongitude;
        const routeLineId = curr.route ?? null;

        if (lat === null || lng === null) return acc;

        const key = `${lat} - ${lng}`;

        if (!acc[key]) {
          acc[key] = {
            lat,
            lng,
            elements: [],
            routeIds: [],
          };
        }

        curr.routeName = getRouteNameFromId(curr, routes);

        acc[key].elements.push(curr);

        if (routeLineId !== null && !acc[key].routeIds.includes(routeLineId)) {
          acc[key].routeIds.push(routeLineId);
        }

        return acc;
      },
      {}
    );

    return Object.values(grouped);
  } else {
    const agreementLines = GenericMapSearchResults.reduce(
      (acc: GenericMapAgreementLine[], curr) => {
        const agreementLine = {
          paSystem: curr.paSystem,
          agreementId: curr.agreementId,
          agreementLineId: curr.agreementLineId,
          agreementLineLatitude:
            curr.agreementLineLatitude || curr.agreementLatitude,
          agreementLineLongitude:
            curr.agreementLineLongitude || curr.agreementLongitude,
          placeNumber: curr.placeNumber,
          routeLineId: curr.routeLineId,
          route: curr.route,
          unitId: curr.unitId,
          unitName: curr.unitName,
          fractionName: curr.fractionName,
          fractionId: curr.fractionId,
          routeIds: [curr.route],
          agreementAddress: curr.agreementAddress,
          routeName: [getRouteNameFromId(curr, routes)],
        };
        acc.push(agreementLine);
        return acc;
      },
      []
    );

    return agreementLines;
  }
}

export function groupDataByRouteLines(
  GenericMapSearchResults: GenericMapSearchResult[]
): GenericMapRouteLine[] {
  const groupedAgreements = GenericMapSearchResults.reduce(
    (acc: { [key: string]: GenericMapRouteLine }, curr) => {
      const key = `${curr.paSystem}_${curr.routeLineId}`;

      if (!acc[key]) {
        acc[key] = {
          routeLineId: curr.routeLineId,
          routeLineLatitude: curr.routeLineLatitude,
          routeLineLongitude: curr.routeLineLongitude,
          agreementId: curr.agreementId,
          agreementLineId: curr.agreementLineId,
          placeNumber: curr.placeNumber,
          route: curr.route,
          paSystem: curr.paSystem,
          routeIds: [curr.route],
        };
      }
      if (acc[key] && !acc[key].routeIds.includes(curr.route)) {
        acc[key].routeIds.push(curr.route);
      }
      return acc;
    },
    {}
  );

  const routesArray = Object.values(groupedAgreements);
  return routesArray;
}

export function calculateDrawnedArea(geojsonObject: Drawing[]): number[] {
  return geojsonObject.map((feature) => {
    if (
      feature.type.toLowerCase() === 'polygon' &&
      feature.coordinates.length > 0
    ) {
      const coordinates = feature.coordinates[0].map((point) => [
        point.lng,
        point.lat,
      ]);

      if (coordinates.length < 3) return 0;

      let area = 0;
      const n = coordinates.length;
      for (let i = 0; i < n; i++) {
        const [x1, y1] = coordinates[i];
        const [x2, y2] = coordinates[(i + 1) % n]; // Ensure the last point connects to the first
        area += x1 * y2 - x2 * y1;
      }
      area = Math.abs(area) / 2;
      return area * (111139 * 111139); // Convert degrees to square meters
    }
    return 0;
  });
}

export function getColumns(gridType: SmartMapGridTypes): ISmartMapGridColumn[] {
  if (gridType === SmartMapGridTypes.AGREEMENTS_GRID) {
    const columns: ISmartMapGridColumn[] = [
      {
        field: 'agreementId',
        filter: 'numeric',
        groupable: false,
        title: 'Id',
      },
      {
        field: 'address',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.grid._address,
      },
      {
        field: 'estate',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.grid._estate,
      },
      {
        field: 'type',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.grid._type,
        width: '60px',
      },
      {
        field: 'routeNames',
        filter: 'text',
        groupable: false,
        title: locale.smartMap._route,
      },
    ];
    return columns;
  } else if (gridType === SmartMapGridTypes.PLACES_GRID) {
    const columns: ISmartMapGridColumn[] = [
      {
        field: 'address',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.grid._address,
      },
      {
        field: 'type',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.grid._type,
      },
    ];
    return columns;
  } else if (gridType === SmartMapGridTypes.BINS_GRID) {
    const columns: ISmartMapGridColumn[] = [
      {
        field: 'name',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.grid._name,
        width: '250px',
      },
      {
        field: 'fraction',
        filter: 'text',
        groupable: false,
        title: locale.smartMap.dashboard._fractions,
        width: '250px',
      },
      {
        field: 'agreementName',
        filter: 'text',
        groupable: true,
        title: locale.smartMap.grid._agreements,
        width: '250px',
      },
    ];
    return columns;
  } else {
    return [];
  }
}

export function createSmartMapElementTooltip(
  elements: (
    | GenericMapAgreement
    | GenericMapAgreementLine
    | GenericMapPlace
    | GenericMapRouteLine
  )[],
  type: SmartMapElementType
): string {
  let infos = '';
  elements.forEach((element) => {
    if (
      'agreementAddress' in element &&
      'agreementEstate' in element &&
      type === SmartMapElementType.AGREEMENTS
    ) {
      infos += `<div class="smart-map-marker-info" style="text-align: left;">
        <strong>${locale.smartMap.grid._address}:</strong> ${
        element.agreementAddress || locale.smartMap.dashboard._unknown
      } <br>
        <strong>${locale.smartMap.grid._estate}:</strong> ${
        element.agreementEstate || locale.smartMap.dashboard._unknown
      } <br>
        <strong>${locale.smartMap.grid._type}:</strong> ${
        element.agreementType || locale.smartMap.dashboard._unknown
      } <br>
        <strong>Id:</strong> ${
          element.agreementId || locale.smartMap.dashboard._unknown
        } <br>
        <strong>${locale.smartMap._route}:</strong> ${
        element.routeNames || locale.smartMap.dashboard._unknown
      } <br>
      </div>`;
    } else if ('unitId' in element && type === SmartMapElementType.BINS) {
      infos += `<div class="smart-map-marker-info" style="text-align: left;">
        <strong>${locale.smartMap.grid._name}:</strong> ${
        element.unitName || locale.smartMap.dashboard._unknown
      } <br>
      <strong>${locale.smartMap.dashboard._fractions}:</strong> ${
        element.fractionName || locale.smartMap.dashboard._unknown
      } <br>
        <strong>${locale.smartMap.grid._address}:</strong> ${
        element.agreementAddress || locale.smartMap.dashboard._unknown
      } <br>
        <strong>${locale.smartMap.grid._agreementId}:</strong> ${
        element.agreementId || locale.smartMap.dashboard._unknown
      } <br>
        <strong>${locale.smartMap._route}:</strong> ${
        element.routeNames || locale.smartMap.dashboard._unknown
      } <br>
      </div>`;
    }
  });

  return infos;
}

export function getRouteNameFromId(
  element: GenericMapSearchResult,
  routeList: IRoute[]
): string {
  const route = routeList.find((route) => route.routeNumbder === element.route);
  return route ? route.name : locale.smartMap.dashboard._unknown;
}

export default {
  groupDataByAgreements,
  groupDataByPlaces,
  groupDataByRoutes,
  selectAgreementLines,
  groupDataByRouteLines,
};
